(() => {
  const OTHER_DESIGNERS = 'Other Designers';
  const OTHER_STYLES = 'Other Styles';
  const VIEW_TITLES = {all: 'ALL SHOES', designer: 'DESIGNERS', category: 'CATEGORIES'};
  const VIEW_FILTER_LABELS = {all: 'SHOP BY STYLE', designer: 'POPULAR DESIGNERS', category: 'SHOP BY STYLE'};
  const GROUP_ORDER = {type: ['running', 'sneakers', 'slides', 'accessories']};
  const DEFAULT_FILTERS = [
    {id: 'sort', label: 'Sort by', source: 'sort', selection: 'single', views: 'all,designer,category'},
    {id: 'category', label: 'Category', source: 'product_type', selection: 'multiple', views: 'all,designer,category'},
    {id: 'designers', label: 'Designers', source: 'designer', selection: 'multiple', views: 'all,designer,category'},
  ];
  const SORT_OPTIONS = [
    ['manual', 'Featured'], ['price-ascending', 'Increasing price'],
    ['created-descending', 'Newest'], ['price-descending', 'Decreasing price'], ['best-selling', 'Best selling'],
  ];
  const COLOUR_SWATCHES = {white: '#fff', black: '#222', grey: '#a8a8a8', gray: '#a8a8a8', blue: '#5e7aa8', purple: '#9173b5', green: '#82a85f', yellow: '#ecff80', red: '#db5a55', coconut: '#e4d6bd', panda: '#222', panther: '#222', ash: '#b9b9b4'};
  const SECTION_CACHE = new Map();
  const SECTION_CACHE_LIMIT = 20;
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const normalizeCategoryKey = (value) => normalize(value).replace(/^gid:\/\/shopify\/metaobject\//, '');
  const normalizeDesignerKey = (value) => normalize(value).replace(/^gid:\/\/shopify\/metaobject\//, '');
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
  const unique = (values) => [...new Set(values.filter(Boolean))];

  class FizoCollection {
    constructor(root) {
      this.root = root;
      this.data = this.readJson('[data-fizo-collection-products]');
      this.categoryProfiles = new Map();
      this.readJson('[data-fizo-category-profiles]').forEach((profile) => {
        if (profile.id) this.categoryProfiles.set(normalizeCategoryKey(profile.id), profile);
        if (profile.name) this.categoryProfiles.set(normalize(profile.name), profile);
      });
      this.designerProfiles = new Map();
      this.readJson('[data-fizo-designer-profiles]').forEach((profile) => {
        if (profile.id) this.designerProfiles.set(normalizeDesignerKey(profile.id), profile);
        if (profile.name) this.designerProfiles.set(normalize(profile.name), profile);
      });
      this.filterConfig = this.readFilterConfig();
      this.cards = [...root.querySelectorAll('[data-fizo-collection-product-card]')];
      this.cardMap = new Map(this.cards.map((card) => [card.dataset.fizoProductId, card]));
      this.events = new AbortController();
      this.allPanel = root.querySelector('[data-fizo-collection-panel="all"]');
      this.allGrid = root.querySelector('[data-fizo-all-grid]');
      this.pagination = root.querySelector('[data-fizo-collection-pagination]');
      this.dynamic = root.querySelector('[data-fizo-collection-dynamic]');
      this.empty = root.querySelector('[data-fizo-collection-empty]');
      this.filters = root.querySelector('[data-fizo-collection-filters]');
      this.globalFilter = root.querySelector('[data-fizo-global-filter]');
      this.globalFilterPanel = root.querySelector('[data-fizo-global-filter-panel]');
      this.globalFilterBackdrop = root.querySelector('[data-fizo-global-filter-backdrop]');
      this.globalFilterOptions = root.querySelector('[data-fizo-global-filter-options]');
      this.globalFilterCount = [...root.querySelectorAll('[data-fizo-global-filter-count]')];
      this.globalFilterApplyCount = root.querySelector('[data-fizo-global-filter-apply-count]');
      this.globalFilterReset = root.querySelector('[data-fizo-global-filter-reset]');
      this.globalFilterOpen = false;
      this.loadingNextPage = false;
      this.loadingCollection = false;
      this.footerVisible = false;
      this.title = root.querySelector('[data-fizo-collection-title]');
      this.filterLabel = root.querySelector('[data-fizo-filter-label]');
      this.showDesignerProfiles = root.dataset.fizoShowDesignerProfiles === 'true';
      this.state = this.readState();
      if (this.hasLegacyFilters()) { this.navigateWithFilters(this.state.filters); return; }
      this.pendingFilters = this.cloneFilters(this.state.filters);
      this.pendingView = this.state.view;
      this.portalGlobalFilter();
      this.observeFooterVisibility();
      this.bind();
      this.render();
      this.setupInfinitePagination();
    }

    readJson(selector) {
      try { return JSON.parse(this.root.querySelector(selector)?.textContent || '[]'); } catch { return []; }
    }

    readFilterConfig() {
      const configured = this.readJson('[data-fizo-collection-filter-config]')
        .filter((filter) => filter.id && filter.source && filter.source !== 'variant_color');
      return configured.length ? configured : DEFAULT_FILTERS;
    }

    cloneFilters(filters) {
      return Object.fromEntries(Object.entries(filters).map(([key, values]) => [key, [...values]]));
    }

    backendParam(filter) {
      if (filter.backendParam) return filter.backendParam;
      if (filter.source === 'sort') return 'sort_by';
      if (filter.source === 'product_type') return this.root.dataset.fizoCategoryFilterParam || 'filter.p.product_type';
      if (filter.source === 'variant_color') return 'filter.v.option.color';
      if (filter.source === 'designer') return 'filter.p.m.custom.designer_master_profile';
      if (filter.source === 'metafield' && filter.namespace && filter.key) return `filter.p.m.${filter.namespace}.${filter.key}`;
      return '';
    }

    filterKey(filter, value) {
      return filter?.source === 'product_type' ? normalizeCategoryKey(value) : normalize(value);
    }

    hasLegacyFilters() {
      const params = new URLSearchParams(window.location.search);
      return (params.has('designer') || params.has('type')) && !this.filterConfig.some((filter) => {
        const param = this.backendParam(filter);
        return param && params.has(param);
      });
    }

    viewForFilter(filter, fallback = this.state.view) {
      if (filter?.source === 'designer') return 'designer';
      if (filter?.source === 'product_type') return 'category';
      return fallback;
    }

    viewForFilters(filters, fallback = this.state.view) {
      const categoryFilter = this.filterConfig.find((filter) => filter.source === 'product_type');
      const designerFilter = this.filterConfig.find((filter) => filter.source === 'designer');
      const hasCategory = Boolean(categoryFilter && (filters[categoryFilter.id] || []).length);
      const hasDesigner = Boolean(designerFilter && (filters[designerFilter.id] || []).length);
      if (hasCategory && hasDesigner) return 'all';
      if (hasCategory) return 'category';
      if (hasDesigner) return 'designer';
      return ['all', 'designer', 'category'].includes(fallback) ? fallback : 'all';
    }

    viewForUrl(url, fallback = this.state.view) {
      const hasCategory = ['filter.p.m.custom.product_categories', 'filter.p.product_type']
        .some((key) => url.searchParams.getAll(key).some(Boolean));
      const hasDesigner = url.searchParams.getAll('filter.p.m.custom.designer_master_profile').some(Boolean);
      if (hasCategory && hasDesigner) return 'all';
      if (hasCategory) return 'category';
      if (hasDesigner) return 'designer';
      return ['all', 'designer', 'category'].includes(fallback) ? fallback : 'all';
    }

    navigateWithFilters(filters, view = this.state.view) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      this.filterConfig.forEach((filter) => {
        url.searchParams.delete(`filter_${filter.id}`);
        if (filter.source === 'designer') url.searchParams.delete('designer');
        if (filter.source === 'product_type') url.searchParams.delete('type');
        const param = this.backendParam(filter);
        if (!param) return;
        url.searchParams.delete(param);
        const values = filters[filter.id] || [];
        if (filter.source === 'sort') {
          if (values[0]) url.searchParams.set('sort_by', values[0]);
        } else {
          const options = this.filterOptions(filter);
          values.forEach((value) => {
            const option = options.find((item) => this.filterKey(filter, item.value) === this.filterKey(filter, value));
            url.searchParams.append(param, option?.value || value);
          });
        }
      });
      url.searchParams.set('view', this.viewForFilters(filters, view));
      this.navigateToUrl(url);
    }

    navigateToNativeFilter(urlValue, view = this.state.view) {
      if (!urlValue) return false;
      const url = new URL(urlValue, window.location.origin);
      url.searchParams.delete('page');
      url.searchParams.set('view', this.viewForUrl(url, view));
      this.navigateToUrl(url);
      return true;
    }

    navigateToView(view) {
      const targetView = ['all', 'designer', 'category'].includes(view) ? view : 'all';
      const url = new URL(this.root.dataset.fizoAllPlpUrl || '/collections/all', window.location.origin);
      url.search = '';
      url.hash = '';
      url.searchParams.set('view', targetView);
      this.navigateToUrl(url);
    }

    sectionRequestUrl(url) {
      const requestUrl = new URL(url, window.location.origin);
      const sectionId = this.root.dataset.fizoSectionId;
      if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
      return requestUrl;
    }

    async fetchSection(url, signal) {
      const requestUrl = this.sectionRequestUrl(url);
      const key = requestUrl.toString();
      if (SECTION_CACHE.has(key)) return SECTION_CACHE.get(key);
      const response = await fetch(key, {headers: {'X-Requested-With': 'XMLHttpRequest'}, credentials: 'same-origin', signal});
      if (!response.ok) throw new Error(`Collection section request failed: ${response.status}`);
      const html = await response.text();
      if (SECTION_CACHE.size >= SECTION_CACHE_LIMIT) SECTION_CACHE.delete(SECTION_CACHE.keys().next().value);
      SECTION_CACHE.set(key, html);
      return html;
    }

    parseCollection(html) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fizo-collection]');
    }

    async navigateToUrl(urlValue, historyMode = 'push') {
      const target = new URL(urlValue, window.location.origin);
      target.searchParams.delete('section_id');
      this.navigationAbort?.abort();
      this.infiniteObserver?.disconnect();
      this.infiniteAbort?.abort();
      const navigationAbort = new AbortController();
      this.navigationAbort = navigationAbort;
      this.loadingCollection = true;
      this.setGlobalFilterOpen(false);
      this.root.classList.add('is-loading');
      this.root.setAttribute('aria-busy', 'true');
      try {
        const nextRoot = this.parseCollection(await this.fetchSection(target, navigationAbort.signal));
        if (!nextRoot) throw new Error('Collection section response is missing the PLP root');
        const previousRoot = this.root;
        this.navigationAbort = null;
        this.destroy();
        if (historyMode === 'push') history.pushState({}, '', `${target.pathname}${target.search}${target.hash}`);
        if (historyMode === 'replace') history.replaceState({}, '', `${target.pathname}${target.search}${target.hash}`);
        previousRoot.replaceWith(nextRoot);
        new FizoCollection(nextRoot);
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(target.toString());
      } finally {
        if (this.navigationAbort === navigationAbort) {
          this.navigationAbort = null;
          this.loadingCollection = false;
        }
      }
    }

    destroy() {
      this.events?.abort();
      this.footerObserver?.disconnect();
      this.infiniteObserver?.disconnect();
      this.infiniteAbort?.abort();
      this.navigationAbort?.abort();
      this.globalFilter?.remove();
      document.documentElement.classList.remove('fizo-collection-filter-open');
      document.body.classList.remove('fizo-collection-filter-open');
    }

    readState() {
      const params = new URLSearchParams(window.location.search);
      const requestedView = ['all', 'designer', 'category'].includes(params.get('view')) ? params.get('view') : 'all';
      const filters = {};
      this.filterConfig.forEach((filter) => {
        const legacyKey = filter.source === 'designer' ? 'designer' : filter.source === 'product_type' ? 'type' : '';
        const currentValue = params.get(`filter_${filter.id}`) || '';
        const legacyValue = legacyKey ? params.get(legacyKey) || '' : '';
        const backendKey = this.backendParam(filter);
        const backendValue = backendKey ? params.getAll(backendKey).join(',') : '';
        // The mega-menu still contains legacy links such as designer=fizo-lab
        // and type=running. Accept them while migrating links to the canonical
        // filter_* parameters. Menu handles use hyphens, while the PLP data
        // stores the human-readable values (for example, "Fizo Lab").
        const raw = currentValue || backendValue || legacyValue.replace(/-/g, ' ');
        filters[filter.id] = unique(raw.split(',').map((value) => this.filterKey(filter, value)));
      });
      const view = this.viewForFilters(filters, requestedView);
      if (view !== requestedView) {
        params.set('view', view);
        const query = params.toString();
        history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      }
      return {view, filters};
    }

    bind() {
      const signal = this.events.signal;
      this.root.addEventListener('fizo:collection-view', (event) => {
        this.navigateToView(event.detail.view);
      }, {signal});

      this.root.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-fizo-colour-swatch]');
        if (swatch) return;

        const viewButton = event.target.closest('[data-fizo-collection-view]');
        if (viewButton) { this.navigateToView(viewButton.dataset.fizoCollectionView); return; }
        const filterButton = event.target.closest('[data-fizo-collection-filter]');
        if (filterButton) {
          const filter = this.filterConfig.find((item) => item.source === filterButton.dataset.fizoCollectionFilter);
          if (!filter) return;
          const valueKey = this.filterKey(filter, filterButton.dataset.fizoCollectionValue);
          const option = this.filterOptions(filter).find((item) => this.filterKey(filter, item.value) === valueKey);
          const selected = this.state.filters[filter.id] || [];
          const targetView = this.viewForFilter(filter);
          if (option && this.navigateToNativeFilter(selected.includes(valueKey) ? option.urlToRemove : option.urlToAdd, targetView)) return;
          this.state.filters[filter.id] = selected[0] === valueKey ? [] : [valueKey];
          this.navigateWithFilters(this.state.filters, targetView); return;
        }

        if (event.target.closest('[data-fizo-clear-filters]')) { this.navigateWithFilters(this.emptyFilters()); return; }

        const carouselControl = event.target.closest('[data-fizo-designer-carousel-control]');
        if (carouselControl) { const carousel = carouselControl.closest('[data-fizo-designer-group]')?.querySelector('[data-fizo-designer-carousel]'); if (carousel) carousel.scrollBy({left: carousel.clientWidth * .82 * (Number(carouselControl.dataset.fizoDesignerCarouselControl) || 1), behavior: 'smooth'}); return; }
        const categoryCarouselControl = event.target.closest('[data-fizo-category-carousel-control]');
        if (categoryCarouselControl) { const carousel = categoryCarouselControl.closest('[data-fizo-category-group]')?.querySelector('[data-fizo-category-carousel]'); if (carousel) carousel.scrollBy({left: carousel.clientWidth * .82 * (Number(categoryCarouselControl.dataset.fizoCategoryCarouselControl) || 1), behavior: 'smooth'}); }
      }, {signal});

      document.addEventListener('click', (event) => {
        const paginationLink = event.target.closest('[data-fizo-collection-pagination] a');
        if (paginationLink) {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const target = new URL(paginationLink.href, window.location.origin);
          const current = new URL(window.location.href);
          const preservedKeys = new Set([...current.searchParams.keys()].filter((key) => key !== 'page'));
          preservedKeys.forEach((key) => target.searchParams.delete(key));
          current.searchParams.forEach((value, key) => { if (key !== 'page') target.searchParams.append(key, value); });
          this.navigateToUrl(target);
          return;
        }
        // The backdrop must consume every outside click before product-card links
        // or other page handlers can act on it.
        if (this.globalFilterOpen && !this.globalFilterPanel?.contains(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setGlobalFilterOpen(false);
          return;
        }

        if (event.target.closest('[data-fizo-global-filter]')) this.handleGlobalFilterEvent(event);
      }, {capture: true, signal});
      document.addEventListener('wheel', (event) => {
        if (this.globalFilterOpen && !this.globalFilterPanel?.contains(event.target)) event.preventDefault();
      }, {passive: false, capture: true, signal});
      document.addEventListener('touchmove', (event) => {
        if (this.globalFilterOpen && !this.globalFilterPanel?.contains(event.target)) event.preventDefault();
      }, {passive: false, capture: true, signal});

      window.addEventListener('popstate', () => this.navigateToUrl(window.location.href, 'none'), {signal});
      window.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.setGlobalFilterOpen(false); }, {signal});
    }

    portalGlobalFilter() {
      if (this.globalFilter && this.globalFilter.parentElement !== document.body) document.body.append(this.globalFilter);
    }

    observeFooterVisibility() {
      const footer = document.querySelector('.fizo-footer[data-footer-reveal-root]');
      if (!footer || !this.globalFilter || !('IntersectionObserver' in window)) return;
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        this.syncGlobalFilterVisibility();
      }, {threshold: 0.01});
      this.footerObserver.observe(footer);
    }

    syncGlobalFilterVisibility() {
      this.globalFilter?.classList.toggle('is-footer-visible', this.footerVisible && !this.globalFilterOpen);
    }

    handleGlobalFilterEvent(event) {
      if (event.target.closest('[data-fizo-global-filter-toggle]')) { event.preventDefault(); this.setGlobalFilterOpen(!this.globalFilterOpen); return; }
      if (event.target.closest('[data-fizo-global-filter-reset]')) { event.preventDefault(); this.navigateWithFilters(this.emptyFilters()); return; }
      if (event.target.closest('[data-fizo-global-filter-close]') || event.target.closest('[data-fizo-global-filter-backdrop]')) { event.preventDefault(); this.setGlobalFilterOpen(false); return; }
      if (event.target.closest('[data-fizo-global-filter-apply]')) {
        event.preventDefault(); this.navigateWithFilters(this.pendingFilters, this.pendingView); return;
      }
      if (event.target.closest('[data-fizo-global-filter-clear]')) { event.preventDefault(); this.pendingFilters = this.emptyFilters(); this.renderFilterDrawer(); return; }
      const drawerOption = event.target.closest('[data-fizo-global-filter-option]');
      if (drawerOption) { event.preventDefault(); this.togglePending(drawerOption.dataset.fizoGlobalFilterOption, drawerOption.dataset.fizoGlobalFilterValue); }
    }

    emptyFilters() { return Object.fromEntries(this.filterConfig.map((filter) => [filter.id, []])); }
    selectedCount(filters = this.state.filters) { return Object.values(filters).reduce((total, values) => total + values.length, 0); }

    setGlobalFilterOpen(open) {
      const shouldOpen = Boolean(open);
      if (shouldOpen === this.globalFilterOpen) return;
      this.globalFilterOpen = shouldOpen;
      if (!this.globalFilter || !this.globalFilterPanel) return;
      if (shouldOpen) {
        this.lockPageScroll();
        this.pendingFilters = this.cloneFilters(this.state.filters);
        this.pendingView = this.state.view;
        this.renderFilterDrawer();
      }
      this.globalFilter.classList.toggle('is-open', this.globalFilterOpen);
      this.globalFilterPanel.hidden = !this.globalFilterOpen;
      if (this.globalFilterBackdrop) this.globalFilterBackdrop.hidden = !this.globalFilterOpen;
      this.globalFilter.querySelector('[data-fizo-global-filter-toggle]')?.setAttribute('aria-expanded', String(this.globalFilterOpen));
      document.documentElement.classList.toggle('fizo-collection-filter-open', this.globalFilterOpen);
      document.body.classList.toggle('fizo-collection-filter-open', this.globalFilterOpen);
      this.syncGlobalFilterVisibility();
      if (!shouldOpen) this.unlockPageScroll();
    }

    lockPageScroll() {
      this.globalFilterScrollY = window.scrollY;
    }

    unlockPageScroll() {
      this.globalFilterScrollY = null;
    }

    writeState() {
      const params = new URLSearchParams(window.location.search);
      params.set('view', this.state.view);
      const query = params.toString();
      history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      this.syncPaginationLinks();
    }

    syncPaginationLinks() {
      const current = new URL(window.location.href);
      current.searchParams.delete('page');
      this.root.querySelectorAll('[data-fizo-collection-pagination] a[href]').forEach((link) => {
        const target = new URL(link.href, window.location.origin);
        const targetPage = target.searchParams.get('page');
        target.search = current.search;
        if (targetPage) target.searchParams.set('page', targetPage);
        else target.searchParams.delete('page');
        link.href = `${target.pathname}${target.search}${target.hash}`;
      });
    }

    nextPageUrl() {
      const link = this.pagination?.querySelector('.pagination__item--prev[href]');
      return link ? new URL(link.href, window.location.origin) : null;
    }

    setupInfinitePagination() {
      if (!this.pagination || !('IntersectionObserver' in window)) return;
      this.pagination.classList.add('is-infinite-scroll');
      this.infiniteStatus = document.createElement('div');
      this.infiniteStatus.className = 'fizo-collection__infinite-status';
      this.infiniteStatus.setAttribute('role', 'status');
      this.infiniteStatus.setAttribute('aria-live', 'polite');
      this.infiniteStatus.hidden = true;
      this.infiniteRetry = document.createElement('button');
      this.infiniteRetry.type = 'button';
      this.infiniteRetry.className = 'fizo-collection__infinite-retry';
      this.infiniteRetry.textContent = 'Retry';
      this.infiniteRetry.hidden = true;
      this.infiniteRetry.addEventListener('click', () => this.loadNextPage(), {signal: this.events.signal});
      this.pagination.append(this.infiniteStatus, this.infiniteRetry);
      if (!this.nextPageUrl()) {
        this.completeInfinitePagination();
        return;
      }
      this.infiniteObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      }, {rootMargin: '600px 0px'});
      this.infiniteObserver.observe(this.pagination);
    }

    setInfiniteStatus(message = '', retry = false) {
      if (this.infiniteStatus) {
        this.infiniteStatus.textContent = message;
        this.infiniteStatus.hidden = !message;
      }
      if (this.infiniteRetry) this.infiniteRetry.hidden = !retry;
      this.root.toggleAttribute('aria-busy', this.loadingNextPage);
    }

    completeInfinitePagination() {
      this.infiniteObserver?.disconnect();
      this.setInfiniteStatus('All products loaded.');
      this.pagination?.classList.add('is-complete');
    }

    mergePage(nextRoot) {
      let incomingData = [];
      try { incomingData = JSON.parse(nextRoot.querySelector('[data-fizo-collection-products]')?.textContent || '[]'); } catch { incomingData = []; }
      const dataIds = new Set(this.data.map((product) => String(product.id)));
      incomingData.forEach((product) => {
        if (dataIds.has(String(product.id))) return;
        dataIds.add(String(product.id));
        this.data.push(product);
      });

      const cardIds = new Set(this.cards.map((card) => card.dataset.fizoProductId));
      nextRoot.querySelectorAll('[data-fizo-collection-product-card]').forEach((card) => {
        const id = card.dataset.fizoProductId;
        if (!id || cardIds.has(id)) return;
        cardIds.add(id);
        this.cards.push(card);
        this.cardMap.set(id, card);
      });

      const incomingPagination = nextRoot.querySelector('[data-fizo-collection-pagination] .pagination-wrapper');
      const currentPagination = this.pagination?.querySelector('.pagination-wrapper');
      if (incomingPagination && currentPagination) currentPagination.replaceWith(incomingPagination);
      else if (incomingPagination) this.pagination?.prepend(incomingPagination);
      else currentPagination?.remove();
      this.syncPaginationLinks();
      this.render();
    }

    async loadNextPage() {
      const nextUrl = this.nextPageUrl();
      if (this.loadingNextPage || !nextUrl) {
        if (!nextUrl) this.completeInfinitePagination();
        return;
      }
      this.loadingNextPage = true;
      this.setInfiniteStatus('Loading more products…');
      try {
        this.infiniteAbort = new AbortController();
        const nextRoot = this.parseCollection(await this.fetchSection(nextUrl, this.infiniteAbort.signal));
        if (!nextRoot) throw new Error('Pagination response is missing the collection');
        this.mergePage(nextRoot);
        this.loadingNextPage = false;
        this.setInfiniteStatus();
        if (!this.nextPageUrl()) this.completeInfinitePagination();
        else requestAnimationFrame(() => {
          const threshold = window.innerHeight + 600;
          if (this.pagination?.getBoundingClientRect().top <= threshold) this.loadNextPage();
        });
      } catch (error) {
        this.loadingNextPage = false;
        if (error.name === 'AbortError') return;
        this.setInfiniteStatus('Unable to load more products.', true);
        console.error('[FIZO PLP] Infinite loading failed', error);
      }
    }

    sourceValues(product, filter) {
      if (filter.source === 'product_type') return this.categoryMemberships(product).map((category) => category.value);
      if (filter.source === 'variant_color') return unique(product.colours || []);
      if (filter.source === 'designer') return product.designer ? [product.designer] : [];
      if (filter.source === 'metafield') {
        const value = product.filterValues?.[filter.id];
        return Array.isArray(value) ? value : value ? String(value).split(',').map((item) => item.trim()) : [];
      }
      return [];
    }

    categoryMemberships(product) {
      if (Array.isArray(product?.categories) && product.categories.length) {
        return product.categories.filter((category) => category?.id || category?.name).map((category) => ({
          value: category.id || category.name,
          label: category.name || this.categoryProfiles.get(normalizeCategoryKey(category.id))?.name || category.id,
        }));
      }
      const value = product?.typeLabel || product?.type;
      return value ? [{value, label: product.typeLabel || product.type}] : [];
    }

    categoryProfile(value, label = '') {
      return this.categoryProfiles.get(normalizeCategoryKey(value)) || this.categoryProfiles.get(normalize(label));
    }

    categoryVisual(value, image = '', label = '') {
      return this.categoryProfile(value, label)?.image || image || '';
    }

    categoryOrder(value, label = '') {
      const configured = Number(this.categoryProfile(value, label)?.sortOrder);
      if (Number.isFinite(configured)) return configured;
      const legacyIndex = GROUP_ORDER.type.indexOf(normalize(label || value));
      return legacyIndex < 0 ? 999 : legacyIndex;
    }

    activeFilters() { return this.filterConfig.filter((filter) => filter.source !== 'sort' && (this.state.filters[filter.id] || []).length); }

    serverFilteringActive() {
      const params = new URLSearchParams(window.location.search);
      return this.filterConfig.some((filter) => this.backendParam(filter) && params.has(this.backendParam(filter)));
    }

    currentProducts() {
      if (this.serverFilteringActive()) return this.data;
      let products = this.data.filter((product) => this.activeFilters().every((filter) => {
        const selected = this.state.filters[filter.id] || [];
        return this.sourceValues(product, filter).some((value) => selected.includes(this.filterKey(filter, value)));
      }));
      const sort = this.filterConfig.find((filter) => filter.source === 'sort');
      const sortValue = sort ? this.state.filters[sort.id]?.[0] : '';
      if (sortValue === 'price-ascending') products = [...products].sort((a, b) => Number(a.price) - Number(b.price));
      if (sortValue === 'price-descending') products = [...products].sort((a, b) => Number(b.price) - Number(a.price));
      if (sortValue === 'created-descending') products = [...products].sort((a, b) => Number(b.publishedAt) - Number(a.publishedAt));
      return products;
    }

    applicableFilters() { return this.filterConfig.filter((filter) => !filter.views || filter.views.split(',').map(normalize).includes(this.state.view)); }

    filterOptions(filter) {
      if (filter.source === 'sort') return SORT_OPTIONS.map(([value, label]) => ({value, label}));
      if (filter.options?.length) return filter.options.map((option) => ({...option, value: option.value}));
      const values = new Map();
      this.data.forEach((product) => this.sourceValues(product, filter).forEach((value) => {
        const key = this.filterKey(filter, value);
        const profile = filter.source === 'product_type' ? this.categoryProfile(value) : null;
        const current = values.get(key) || {value, label: profile?.name || value, count: 0}; current.count += 1; values.set(key, current);
      }));
      const options = [...values.values()].sort((a, b) => a.label.localeCompare(b.label));
      return filter.source === 'product_type' ? [{value: '__all__', label: 'All', count: this.data.length, isAll: true}, ...options] : options;
    }

    togglePending(id, rawValue) {
      const filter = this.filterConfig.find((item) => item.id === id);
      if (!filter) return;
      this.pendingView = this.viewForFilter(filter, this.pendingView);
      const value = this.filterKey(filter, rawValue);
      if (value === '__all__') {
        const allValues = this.filterOptions(filter)
          .filter((option) => !option.isAll)
          .map((option) => this.filterKey(filter, option.value));
        const selected = this.pendingFilters[id] || [];
        const isEverythingSelected = allValues.length > 0 && allValues.every((option) => selected.includes(option));
        this.pendingFilters[id] = isEverythingSelected ? [] : allValues;
        this.renderFilterDrawer();
        return;
      }
      const values = this.pendingFilters[id] || [];
      this.pendingFilters[id] = filter.selection === 'single' ? (values.includes(value) ? [] : [value]) : (values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
      this.renderFilterDrawer();
    }

    renderFilterDrawer() {
      if (!this.globalFilterOptions) return;
      const groups = this.applicableFilters().map((filter) => {
        const selected = this.pendingFilters[filter.id] || [];
        const options = this.filterOptions(filter);
        const controls = options.map((option) => {
          const active = option.isAll
            ? options.filter((item) => !item.isAll).every((item) => selected.includes(this.filterKey(filter, item.value)))
            : selected.includes(this.filterKey(filter, option.value));
          const count = option.count ? `<sup>${option.count}</sup>` : '';
          const colour = filter.source === 'variant_color' ? `<i style="--fizo-colour-swatch:${COLOUR_SWATCHES[normalize(option.value)] || '#f7f7f7'}"></i>` : '';
          const categoryImage = filter.source === 'product_type' ? this.categoryVisual(option.value, option.image, option.label) : '';
          return `<button type="button" data-fizo-global-filter-option="${escapeHtml(filter.id)}" data-fizo-global-filter-value="${escapeHtml(option.value)}" class="${option.isAll ? 'is-all ' : ''}${active ? 'is-active' : ''}" aria-pressed="${active}">${categoryImage ? `<img src="${escapeHtml(categoryImage)}" alt="" loading="lazy">` : colour}<span>${escapeHtml(option.label)}${count}</span></button>`;
        }).join('');
        return `<section class="fizo-collection__filter-group fizo-collection__filter-group--${escapeHtml(filter.source)}"><h3><i></i>${escapeHtml(filter.label)}</h3><div class="fizo-collection__filter-options">${controls}</div></section>`;
      }).join('');
      this.globalFilterOptions.innerHTML = groups;
      const count = this.selectedCount(this.pendingFilters);
      this.globalFilterCount.forEach((node) => { node.textContent = String(count); });
      if (this.globalFilterApplyCount) this.globalFilterApplyCount.textContent = String(count);
      if (this.globalFilterReset) this.globalFilterReset.disabled = this.selectedCount(this.state.filters) === 0;
    }

    syncControls() {
      this.root.querySelectorAll('[data-fizo-collection-view]').forEach((button) => { const active = button.dataset.fizoCollectionView === this.state.view; button.classList.toggle('is-active', active); button.setAttribute('aria-selected', String(active)); });
      if (this.title) this.title.textContent = VIEW_TITLES[this.state.view];
      if (this.filterLabel) this.filterLabel.textContent = VIEW_FILTER_LABELS[this.state.view];
    }

    renderFilters() {
      if (!this.filters) return;
      const source = this.state.view === 'designer' ? 'designer' : 'product_type';
      const filter = this.filterConfig.find((item) => item.source === source);
      if (!filter) { this.filters.innerHTML = ''; return; }
      const selected = this.state.filters[filter.id] || [];
      // "All" is a drawer-only multi-select control, never a quick-filter value.
      this.filters.innerHTML = this.filterOptions(filter).filter((option) => !option.isAll).sort((left, right) => source === 'designer'
        ? right.count - left.count || left.label.localeCompare(right.label)
        : this.categoryOrder(left.value, left.label) - this.categoryOrder(right.value, right.label) || left.label.localeCompare(right.label)).map((option) => {
        const optionKey = this.filterKey(filter, option.value);
        const product = this.data.find((item) => this.sourceValues(item, filter).map((value) => this.filterKey(filter, value)).includes(optionKey));
        const image = source === 'designer' ? this.designerVisual(product, option.value) : this.categoryVisual(option.value, option.image, option.label);
        const profile = source === 'designer' ? this.designerProfiles.get(normalizeDesignerKey(option.value)) : null;
        const displayLabel = profile?.name || option.label;
        return `<button type="button" data-fizo-collection-filter="${escapeHtml(source)}" data-fizo-collection-value="${escapeHtml(option.value)}" class="${selected.includes(optionKey) ? 'is-active' : ''}">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}<span>${escapeHtml(displayLabel)}</span></button>`;
      }).join('');
      this.renderFilterDrawer();
    }

    cardFor(product) { return this.cardMap.get(String(product.id)); }
    moveCards(grid, products) { products.forEach((product) => { const card = this.cardFor(product); if (card) { card.hidden = false; grid.append(card); } }); }
    cloneCards(grid, products) { products.forEach((product) => { const card = this.cardFor(product); if (card) { const clone = card.cloneNode(true); clone.hidden = false; grid.append(clone); } }); }
    designerVisual(product, designer = '') {
      const profile = this.designerProfiles.get(normalizeDesignerKey(designer || product?.designer));
      if (profile) return profile.brandIcon || profile.profileImage || '';
      const card = product && this.cardFor(product);
      return card?.dataset.fizoBrandIcon || card?.dataset.fizoDesignerImage || '';
    }

    profileFor(product, label, count) {
      const profile = this.designerProfiles.get(normalizeDesignerKey(product?.designer)); const card = product && this.cardFor(product); const image = profile?.profileImage || card?.dataset.fizoDesignerImage; const role = profile?.role || card?.dataset.fizoDesignerRole; const bio = profile?.bio || card?.dataset.fizoDesignerBio; const year = profile?.year || card?.dataset.fizoDesignerYear; const profileName = profile?.name || card?.dataset.fizoDesignerProfileName || label;
      return `<li class="fizo-collection-designer-profile">${image ? `<img class="fizo-collection-designer-profile__image" src="${escapeHtml(image)}" alt="${escapeHtml(profileName)}">` : ''}<div class="fizo-collection-designer-profile__content"><p class="fizo-collection-designer-profile__eyebrow">Profile</p><h2>${escapeHtml(profileName)}</h2>${role ? `<p class="fizo-collection-designer-profile__role">${escapeHtml(role)}</p>` : ''}${bio ? `<p class="fizo-collection-designer-profile__role">${escapeHtml(bio)}</p>` : ''}<p class="fizo-collection-designer-profile__stats"><span>Designs: ${count}</span>${year ? `<span>Joined: ${escapeHtml(year)}</span>` : ''}</p></div></li>`;
    }

    categoryPromotion(position) { const template = this.root.querySelector('[data-fizo-category-promo-template]'); return template && Number(template.dataset.fizoCategoryPromoPosition) === position ? template.content.firstElementChild?.cloneNode(true) : null; }

    groupTotal(kind, groupKey, groupLabel, fallback) {
      if (String(groupKey).startsWith('__')) return fallback;
      const source = kind === 'designer' ? 'designer' : 'product_type';
      const filter = this.filterConfig.find((item) => item.source === source);
      if (!filter) return fallback;
      const key = source === 'product_type' ? normalizeCategoryKey(groupKey) : normalize(groupKey);
      const label = normalize(groupLabel);
      const option = this.filterOptions(filter).find((item) => this.filterKey(filter, item.value) === key || normalize(item.label) === label);
      const total = Number(option?.count);
      return Number.isFinite(total) ? total : fallback;
    }

    groupUrl(kind, groupKey, groupLabel) {
      const isDesigner = kind === 'designer';
      const filter = this.filterConfig.find((item) => item.source === (isDesigner ? 'designer' : 'product_type'));
      const option = filter && this.filterOptions(filter).find((item) => this.filterKey(filter, item.value) === this.filterKey(filter, groupKey) || normalize(item.label) === normalize(groupLabel));
      const url = new URL(option?.urlToAdd || this.root.dataset.fizoAllPlpUrl || '/collections/all', window.location.origin);
      if (!option?.urlToAdd) url.search = '';
      url.searchParams.set('view', isDesigner ? 'designer' : 'category');
      if (!option?.urlToAdd && !String(groupKey).startsWith('__')) {
        url.searchParams.set(this.backendParam(filter), groupKey);
      }
      return `${url.pathname}${url.search}`;
    }

    renderGrouped(products, kind) {
      const groups = new Map();
      const categoryFilter = kind === 'type' ? this.filterConfig.find((filter) => filter.source === 'product_type') : null;
      const selectedCategories = categoryFilter ? this.state.filters[categoryFilter.id] || [] : [];
      products.forEach((product) => {
        const memberships = kind === 'designer'
          ? [{value: product.designer || '__other-designers', label: product.designerLabel || OTHER_DESIGNERS}]
          : this.categoryMemberships(product);
        const visibleMemberships = selectedCategories.length
          ? memberships.filter((membership) => selectedCategories.includes(this.filterKey(categoryFilter, membership.value)))
          : memberships;
        (visibleMemberships.length ? visibleMemberships : kind === 'designer' ? memberships : [{value: '__other-styles', label: OTHER_STYLES}]).forEach((membership) => {
          const key = membership.value;
          if (!groups.has(key)) groups.set(key, {label: membership.label, products: []});
          groups.get(key).products.push(product);
        });
      });
      this.dynamic.innerHTML = '';
      const groupedEntries = [...groups.entries()].map(([groupKey, group]) => ({groupKey, group, total: this.groupTotal(kind, groupKey, group.label, group.products.length)}));
      groupedEntries.sort((left, right) => kind === 'designer'
        ? right.total - left.total || left.group.label.localeCompare(right.group.label)
        : this.categoryOrder(left.groupKey, left.group.label) - this.categoryOrder(right.groupKey, right.group.label) || left.group.label.localeCompare(right.group.label)).forEach(({groupKey, group, total}, groupIndex) => {
        const isDesigner = kind === 'designer'; const section = document.createElement('section'); const firstCard = this.cardFor(group.products[0]); const profile = isDesigner ? this.designerProfiles.get(normalizeDesignerKey(groupKey)) : this.categoryProfile(groupKey, group.label); const descriptionHtml = profile?.descriptionHtml || ''; const description = profile?.description || (isDesigner ? firstCard?.dataset.fizoCollectionDescription : '') || '';
        section.className = `fizo-collection__group${kind === 'type' ? ' fizo-collection__group--category' : ''}${isDesigner ? ' fizo-collection__group--designer' : ''}`; if (isDesigner) section.dataset.fizoDesignerGroup = ''; else section.dataset.fizoCategoryGroup = '';
        const controls = isDesigner ? `<button type="button" data-fizo-designer-carousel-control="-1" aria-label="Previous ${escapeHtml(group.label)} products">‹</button><button type="button" data-fizo-designer-carousel-control="1" aria-label="Next ${escapeHtml(group.label)} products">›</button>` : `<button type="button" data-fizo-category-carousel-control="-1" aria-label="Previous ${escapeHtml(group.label)} products">‹</button><button type="button" data-fizo-category-carousel-control="1" aria-label="Next ${escapeHtml(group.label)} products">›</button>`;
        const carousel = isDesigner ? `<div class="fizo-designer-carousel-layout">${this.showDesignerProfiles ? this.profileFor(group.products[0], group.label, total) : ''}<div class="fizo-designer-carousel" data-fizo-designer-carousel><ul class="fizo-designer-carousel__track" role="list"></ul></div></div>` : `<div class="fizo-category-carousel" data-fizo-category-carousel><ul class="fizo-category-carousel__track" role="list"></ul></div>`;
        const viewAllUrl = this.groupUrl(kind, groupKey, group.label);
        const descriptionMarkup = descriptionHtml ? `<div class="fizo-collection__group-description">${descriptionHtml}</div>` : description ? `<p class="fizo-collection__group-description">${escapeHtml(description)}</p>` : '<span></span>';
        section.innerHTML = `<div class="fizo-collection__group-head"><h2 class="fizo-collection__group-title"><a href="${escapeHtml(viewAllUrl)}">${escapeHtml(group.label)}<sup>${total}</sup></a></h2><div class="fizo-collection__group-summary">${descriptionMarkup}<div class="fizo-collection__group-actions"><a href="${escapeHtml(viewAllUrl)}">View All</a><span class="fizo-collection__group-controls">${controls}</span></div></div></div>${carousel}`;
        const track = section.querySelector(isDesigner ? '.fizo-designer-carousel__track' : '.fizo-category-carousel__track'); isDesigner ? this.moveCards(track, group.products) : this.cloneCards(track, group.products); this.dynamic.append(section); isDesigner ? this.bindDesignerCarousel(section) : this.bindCategoryCarousel(section);
        if (!isDesigner && !this.activeFilters().length) { const promo = this.categoryPromotion(groupIndex + 1); if (promo) this.dynamic.append(promo); }
      });
    }

    bindDesignerCarousel(section) { const carousel = section.querySelector('[data-fizo-designer-carousel]'); const layout = section.querySelector('.fizo-designer-carousel-layout'); const track = carousel?.querySelector('.fizo-designer-carousel__track'); const profile = layout?.querySelector('.fizo-collection-designer-profile'); const previous = section.querySelector('[data-fizo-designer-carousel-control="-1"]'); const next = section.querySelector('[data-fizo-designer-carousel-control="1"]'); if (!carousel || !track || !previous || !next) return; const sync = () => { const remaining = carousel.scrollWidth - carousel.clientWidth - carousel.scrollLeft; previous.disabled = carousel.scrollLeft <= 2; next.disabled = remaining <= 2; }; const mobile = window.matchMedia('(max-width: 749px)'); const placeProfile = () => { if (!profile || !layout) return; if (mobile.matches) track.prepend(profile); else layout.insertBefore(profile, carousel); carousel.scrollLeft = 0; requestAnimationFrame(sync); }; carousel.addEventListener('scroll', sync, {passive: true}); mobile.addEventListener('change', placeProfile); placeProfile(); }
    bindCategoryCarousel(section) { const carousel = section.querySelector('[data-fizo-category-carousel]'); const previous = section.querySelector('[data-fizo-category-carousel-control="-1"]'); const next = section.querySelector('[data-fizo-category-carousel-control="1"]'); if (!carousel || !previous || !next) return; const sync = () => { const remaining = carousel.scrollWidth - carousel.clientWidth - carousel.scrollLeft; previous.disabled = carousel.scrollLeft <= 2; next.disabled = remaining <= 2; }; carousel.addEventListener('scroll', sync, {passive: true}); requestAnimationFrame(sync); }

    render() {
      this.syncControls(); this.syncPaginationLinks(); this.renderFilters(); const products = this.currentProducts(); const isAll = this.state.view === 'all'; this.allPanel.hidden = !isAll; this.dynamic.hidden = isAll; this.empty.hidden = products.length > 0;
      if (isAll) {
        const visibleIds = new Set(products.map((product) => String(product.id)));
        this.cards.forEach((card) => { card.hidden = !visibleIds.has(card.dataset.fizoProductId); });
        this.moveCards(this.allGrid, products);
      } else {
        this.cards.forEach((card) => { card.hidden = false; });
        this.renderGrouped(products, this.state.view === 'designer' ? 'designer' : 'type');
      }
    }
  }

  const init = () => { document.querySelectorAll('[data-fizo-collection]').forEach((root) => new FizoCollection(root)); document.addEventListener('click', (event) => { const link = event.target.closest('[data-fizo-plp-view]'); const root = document.querySelector('[data-fizo-collection]'); if (!link || !root) return; const target = new URL(link.href, window.location.origin); if (target.pathname !== window.location.pathname) return; event.preventDefault(); root.dispatchEvent(new CustomEvent('fizo:collection-view', {detail: {view: link.dataset.fizoPlpView}})); }); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init, {once: true}) : init();
})();
